import { storableError } from '../util/errors';
import {
  clearStreamChatNotificationsApiCache,
  getStreamChatNotificationsApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
} from '../util/api';
import { fetchNotificationsFeed } from '../util/notificationsFeed';
import {
  filterDisplayableNotifications,
  getUnreadCountForDisplayableNotifications,
} from '../containers/TopbarContainer/Topbar/NotificationsPanel/notificationVisibility';
import { mapLegacyAttentionTransactionsToNotifications } from '../containers/TopbarContainer/Topbar/NotificationsPanel/legacyAttentionNotifications';
import { mergeNotificationFeed } from '../util/notificationHelpers';

export const FETCH_NOTIFICATIONS_STATE_REQUEST = 'app/notifications/FETCH_STATE_REQUEST';
export const FETCH_NOTIFICATIONS_STATE_SUCCESS = 'app/notifications/FETCH_STATE_SUCCESS';
export const FETCH_NOTIFICATIONS_STATE_ERROR = 'app/notifications/FETCH_STATE_ERROR';
export const SET_NOTIFICATIONS_UNREAD_COUNT = 'app/notifications/SET_UNREAD_COUNT';
const NOTIFICATIONS_FEED_PAGE_SIZE = 100;

const initialState = {
  unreadCount: 0,
  fetchInProgress: false,
  fetchError: null,
};
let fetchNotificationsStateRequestPromise = null;
let fetchNotificationsStateRequestMode = {
  forceNotificationsFresh: false,
  forceStreamFresh: false,
  cacheScope: null,
};
let fetchNotificationsStateRequestVersion = 0;

export const resetNotificationsStateRuntime = () => {
  fetchNotificationsStateRequestPromise = null;
  fetchNotificationsStateRequestMode = {
    forceNotificationsFresh: false,
    forceStreamFresh: false,
  };
  fetchNotificationsStateRequestVersion += 1;
  clearStreamChatNotificationsApiCache();
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_NOTIFICATIONS_STATE_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_NOTIFICATIONS_STATE_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        unreadCount: payload.unreadCount,
      };
    case FETCH_NOTIFICATIONS_STATE_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };
    case SET_NOTIFICATIONS_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: payload.unreadCount,
      };
    default:
      return state;
  }
}

const fetchNotificationsStateRequest = () => ({
  type: FETCH_NOTIFICATIONS_STATE_REQUEST,
});

const fetchNotificationsStateSuccess = unreadCount => ({
  type: FETCH_NOTIFICATIONS_STATE_SUCCESS,
  payload: { unreadCount },
});

const fetchNotificationsStateError = error => ({
  type: FETCH_NOTIFICATIONS_STATE_ERROR,
  payload: error,
  error: true,
});

export const setNotificationsUnreadCount = unreadCount => ({
  type: SET_NOTIFICATIONS_UNREAD_COUNT,
  payload: { unreadCount: Number(unreadCount) || 0 },
});

const getLegacyAttentionNotifications = state => {
  const user = state?.user || {};
  return mapLegacyAttentionTransactionsToNotifications({
    transactions: user.currentUserAttentionTransactions || [],
    currentUser: user.currentUser || null,
  });
};

const getCurrentUserId = state => state?.user?.currentUser?.id?.uuid || null;

const canReuseInFlightRequest = (currentMode, nextMode) => {
  if (!fetchNotificationsStateRequestPromise) {
    return false;
  }

  const notificationsCompatible =
    !nextMode.forceNotificationsFresh || currentMode.forceNotificationsFresh;
  const streamCompatible = !nextMode.forceStreamFresh || currentMode.forceStreamFresh;
  const scopeCompatible = currentMode.cacheScope === nextMode.cacheScope;

  return notificationsCompatible && streamCompatible && scopeCompatible;
};

export const fetchNotificationsState = (options = {}) => (dispatch, getState = () => ({})) => {
  const {
    forceNotificationsFresh = false,
    forceStreamFresh = false,
    reason = 'initial-load',
  } = options;
  const cacheScope = getCurrentUserId(getState());
  const requestMode = {
    forceNotificationsFresh: !!forceNotificationsFresh,
    forceStreamFresh: !!forceStreamFresh,
    cacheScope,
  };

  if (canReuseInFlightRequest(fetchNotificationsStateRequestMode, requestMode)) {
    return fetchNotificationsStateRequestPromise;
  }

  dispatch(fetchNotificationsStateRequest());
  fetchNotificationsStateRequestMode = requestMode;
  const requestVersion = fetchNotificationsStateRequestVersion + 1;
  fetchNotificationsStateRequestVersion = requestVersion;
  const notificationsFeedOptions = cacheScope ? { cacheScope } : {};
  const streamNotificationsOptions = cacheScope ? { cacheScope } : {};

  fetchNotificationsStateRequestPromise = Promise.allSettled([
    fetchNotificationsFeed({
      limit: NOTIFICATIONS_FEED_PAGE_SIZE,
      reason,
      forceFresh: !!forceNotificationsFresh,
      ...notificationsFeedOptions,
    }),
    getStreamChatNotificationsApi({
      forceFresh: forceStreamFresh,
      ...streamNotificationsOptions,
    }),
  ])
    .then(([notificationsResult, streamNotificationsResult]) => {
      const state = getState();
      const currentUser = state?.user?.currentUser || null;
      const legacyAttentionNotifications = getLegacyAttentionNotifications(state);

      let notificationsItems = [];
      if (notificationsResult.status === 'fulfilled') {
        notificationsItems = filterDisplayableNotifications(
          notificationsResult.value?.items || [],
          currentUser
        );
      }

      const streamPayload =
        streamNotificationsResult.status === 'fulfilled'
          ? streamNotificationsResult.value?.data || streamNotificationsResult.value || {}
          : {};
      const shouldUseLegacyAttention =
        notificationsResult.status === 'rejected' && legacyAttentionNotifications.length > 0;
      const unreadCount = getUnreadCountForDisplayableNotifications(
        mergeNotificationFeed({
          notifications: shouldUseLegacyAttention
            ? legacyAttentionNotifications
            : notificationsItems,
          messageNotifications:
            streamPayload.notifications || streamPayload.unreadNotifications || [],
        }),
        currentUser
      );

      if (
        notificationsResult.status === 'rejected' &&
        streamNotificationsResult.status === 'rejected'
      ) {
        const error = notificationsResult.reason || streamNotificationsResult.reason;
        throw error;
      }

      if (notificationsResult.status === 'rejected') {
        console.error(notificationsResult.reason); // eslint-disable-line
      }

      if (streamNotificationsResult.status === 'rejected') {
        console.error(streamNotificationsResult.reason); // eslint-disable-line
      }

      if (fetchNotificationsStateRequestVersion === requestVersion) {
        dispatch(fetchNotificationsStateSuccess(unreadCount));
      }
      return unreadCount;
    })
    .catch(error => {
      if (fetchNotificationsStateRequestVersion === requestVersion) {
        dispatch(fetchNotificationsStateError(storableError(error)));
      }
      throw error;
    })
    .finally(() => {
      if (fetchNotificationsStateRequestVersion === requestVersion) {
        fetchNotificationsStateRequestPromise = null;
        fetchNotificationsStateRequestMode = {
          forceNotificationsFresh: false,
          forceStreamFresh: false,
          cacheScope: null,
        };
      }
    });

  return fetchNotificationsStateRequestPromise;
};

export const markNotificationAsRead = notificationId => dispatch => {
  return markNotificationReadApi(notificationId).then(response => {
    return dispatch(
      fetchNotificationsState({
        forceNotificationsFresh: true,
        forceStreamFresh: true,
        reason: 'single-read',
      })
    ).then(() => response);
  });
};

export const markAllNotificationsAsRead = () => dispatch => {
  return markAllNotificationsReadApi().then(response => {
    return dispatch(
      fetchNotificationsState({
        forceNotificationsFresh: true,
        forceStreamFresh: true,
        reason: 'mark-all-read',
      })
    ).then(() => response);
  });
};
